import * as React from 'react';
import { useEffect, useState } from 'react';

import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarColumnsButton,
  GridToolbarFilterButton
} from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import BeenhereIcon from '@mui/icons-material/Beenhere';
import UndoIcon from '@mui/icons-material/Undo';

// Components and functions for the project
import ThemeColors from '../../../../shared/components/ThemeColors/ThemeColors';
import axios from 'axios';
import use123zapStore from '../../../../context/123zapStore';
import dayjs from 'dayjs';
import { fetchAuthSession } from 'aws-amplify/auth';

// Render The Component
export default function ExcelTable({
  rows,
  setRows,
  columns,
  setColumns,
  startDate,
  endDate,
  sentMessagesLength,
  setSentMessagesLength,
  totalApiPages,
  setTotalApiPages,
  campaignFilterName = '',
}) {
  const [selectedRows, setSelectedRows] = useState([]);
  const [history, setHistory] = useState([]);
  const [currentRows, setCurrentRows] = useState(rows);
  const [loading, setLoading] = useState(false);
  const [apiPage, setApiPage] = useState(1);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100
  });
  const [receivedPaginationModel, setReceivedPaginationModel] = useState({
    page: 0,
    pageSize: 100
  });
  const fetchedApiPages = React.useRef(new Set());

  const showReceived = use123zapStore((state) => state.showReceived);
  const setShowReceived = use123zapStore((state) => state.setShowReceived);
  const receivedMessages = use123zapStore((state) => state.receivedMessages);
  const setReceivedMessages = use123zapStore((state) => state.setReceivedMessages);

  useEffect(() => {
    setCurrentRows(rows);
  }, [rows]);

  useEffect(() => {
    setSelectedRows([]);
    setHistory([]);
    setPaginationModel({ page: 0, pageSize: 100 });
    setApiPage(1);
    fetchedApiPages.current = new Set();
  }, [campaignFilterName, startDate, endDate]);

  // Função para deletar as linhas selecionadas
  const handleDelete = () => {
    setHistory(prevHistory => [...prevHistory, currentRows]);  // Registra o estado atual no histórico
    setCurrentRows(prev => prev.filter(row => !selectedRows.includes(row.id)));
    setRows(prev => prev.filter(row => !selectedRows.includes(row.id)));
    setSelectedRows([]);
  };

  // Função para manter somente as linhas selecionadas
  const handleRowSelection = () => {
    setHistory(prevHistory => [...prevHistory, currentRows]);  // Registra o estado atual no histórico
    setCurrentRows(prev => prev.filter(row => selectedRows.includes(row.id)));
    setRows(prev => prev.filter(row => selectedRows.includes(row.id)));
    setSelectedRows([]);
  };

  // Função para reverter as alterações
  const handleRevert = () => {
    if (history.length > 0) {
      const lastState = history[history.length - 1];
      setCurrentRows(lastState);  // Restaura o estado anterior
      setRows(lastState);  // Atualiza as linhas no estado pai
      setHistory(history.slice(0, -1));  // Remove o último estado do histórico
    }
  };

  const handleFetchReceived = async () => {
    setLoading(true);

    const body = {
      start_date: startDate.format('DD/MM/YYYY'),
      end_date: endDate.format('DD/MM/YYYY'),
      refresh: true
    };

    const session = await fetchAuthSession({ forceRefresh: true });

    const token = session.tokens.accessToken.toString();
    const client_id = session.tokens.idToken.toString();

    const url = process.env.REACT_APP_BACKEND_AWS_URL;

    axios.post(`${url}/dashboard/messages/received/lines`, body, {
      headers: { Authorization: token, "Client_ID": client_id }
    })
      .then((response) => {

        if (response.data.data.length > 0) {
          const mappedData = response.data.data.map((item) => ({
            'Nome do contato': item.pushname.split(' ')[0],
            'Contato': item.remotejid || item.owner,
            'Mensagem': item.conversation.length > 50 ? item.conversation.substring(0, 40) + '...' : item.conversation,
            'Data de recebimento': item.timestamp,
            'Status/Entrega': 'recebida',
            'CPF/CNPJ': item.cpf_cnpj
          }));

          const dynamicColumns = [
            'Data de recebimento',
            'Nome do contato',
            'CPF/CNPJ',
            'Contato',
            'Mensagem',
            'Status/Entrega'
          ].map((key) => {
            const normalizeHeader = (str) =>
              str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

            const normalizedKey = normalizeHeader(key);

            if (normalizedKey === 'Status/Entrega') {
              const statusStyles = {
                'entrega não confirmada': {
                  color: 'warning',
                  backgroundColor: '#fff3cd',
                  tooltipMessage: 'O sistema do WhatsApp aceitou sua mensagem, mas ainda não confirmou a entrega ao destinatário.'
                },
                espera: {
                  color: 'info',
                  backgroundColor: '#e1f7ff',
                  tooltipMessage: 'Sua mensagem foi criada e está aguardando envio.'
                },
                falha: {
                  color: 'error',
                  backgroundColor: '#f8d7da',
                  tooltipMessage: 'Ocorreu uma falha no envio.'
                },
                entregue: {
                  color: 'primary',
                  backgroundColor: '#d1ecf1',
                  tooltipMessage: 'Sua mensagem já foi entregue ao destinatário.'
                },
                recebida: {
                  color: 'success',
                  backgroundColor: '#E9F5E3',
                  tooltipMessage: 'O destinatário recebeu sua mensagem.'
                },
                desconhecido: {
                  color: 'default',
                  backgroundColor: '#f1f1f1',
                  tooltipMessage: 'Não foi possível gerar o item. Tente novamente mais tarde.'
                },
                default: {
                  color: 'default',
                  backgroundColor: '#f1f1f1',
                  tooltipMessage: 'Status desconhecido.'
                }
              };

              return {
                field: key,
                headerName: key,
                flex: 1,
                minWidth: 180,
                renderCell: (params) => {
                  const value = params.value || 'desconhecido';
                  const status = statusStyles[value.toLowerCase()] || statusStyles.default;

                  return (
                    <Tooltip
                      title={
                        <Typography sx={{ fontSize: '14px', alignContent: 'center', textAlign: 'center' }}>
                          {status.tooltipMessage}
                        </Typography>
                      }
                      arrow
                    >
                      <Chip
                        label={value}
                        color={status.color}
                        variant="outlined"
                        sx={{
                          fontWeight: 'bold',
                          borderRadius: '8px',
                          backgroundColor: status.backgroundColor,
                          textTransform: 'capitalize',
                          minWidth: 100,
                          padding: '4px 8px',
                          cursor: 'pointer'
                        }}
                      />
                    </Tooltip>

                  );
                }
              };
            }

            return {
              field: key,
              headerName: key,
              flex: 1,
              minWidth: 150
            }
          });

          const formattedData = mappedData.map((item, index) => ({
            id: index + 1,
            ...item
          }));

          if (!Array.isArray(dynamicColumns) || !Array.isArray(formattedData)) {
            console.error('Erro: dynamicColumns ou formattedData não são arrays válidos');
            return;
          }

          setReceivedMessages({
            columns: dynamicColumns,
            rows: formattedData
          });

          setShowReceived(true);
        }

      })
      .catch((error) => {
        console.error('Erro ao buscar os indicadores:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton
        slotProps={{
          button: {
            sx: {
              backgroundColor: '#FFF',
              color: ThemeColors.purple900,
              border: `1px solid ${ThemeColors.purple600}`,
              padding: '15px',
              height: '40px', // Definindo a altura fixa
              '&:hover': {
                backgroundColor: ThemeColors.purple900,
                color: '#FFF',
              },
              '&:disabled': {
                backgroundColor: '#DDD4FF',
                color: '#A986FF',
                border: `none`,
              },
            },
          },
        }}
      />
      <GridToolbarFilterButton
        slotProps={{
          button: {
            sx: {
              backgroundColor: '#FFF',
              color: ThemeColors.purple900,
              border: `1px solid ${ThemeColors.purple600}`,
              padding: '15px',
              height: '40px', // Definindo a altura fixa
              '&:hover': {
                backgroundColor: ThemeColors.purple900,
                color: '#FFF',
              },
              '&:disabled': {
                backgroundColor: '#DDD4FF',
                color: '#A986FF',
                border: `none`,
              },
            },
          },
        }}
      />
      <GridToolbarExport
        slotProps={{
          button: {
            sx: {
              backgroundColor: '#FFF',
              color: ThemeColors.purple900,
              border: `1px solid ${ThemeColors.purple600}`,
              padding: '15px',
              height: '40px', // Definindo a altura fixa
              '&:hover': {
                backgroundColor: ThemeColors.purple900,
                color: '#FFF',
              },
              '&:disabled': {
                backgroundColor: '#DDD4FF',
                color: '#A986FF',
                border: `none`,
              },
            },
          },
        }}
      />

      <Button
        variant="contained"
        sx={{
          backgroundColor: '#FFF',
          color: ThemeColors.purple500,
          border: `1px solid ${ThemeColors.purple500}`,
          padding: '15px',
          height: '40px', // Definindo a altura fixa
          '&:hover': {
            backgroundColor: ThemeColors.purple900,
            color: '#FFF',
          },
          '&:disabled': {
            backgroundColor: '#DDD4FF',
            color: '#A986FF',
            border: `none`,
          }
        }}
        onClick={showReceived ? () => setShowReceived(false) : handleFetchReceived}
        disabled={loading}
      >
        {loading ? 'Consultando...' : showReceived ? 'Consultar Enviadas' : 'Consultar Recebidas'}
      </Button>

      {
        showReceived === false ? (
          <>
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#FFF',
                color: ThemeColors.red03,
                border: `1px solid ${ThemeColors.red03}`,
                padding: '15px',
                height: '40px', // Definindo a altura fixa
                '&:hover': {
                  backgroundColor: ThemeColors.red01,
                  color: ThemeColors.red03,
                },
                '&:disabled': {
                  backgroundColor: '#DDD4FF',
                  color: '#A986FF',
                  border: `none`,
                },
                '&:not(.Mui-disabled) .MuiSvgIcon-root': {
                  color: ThemeColors.red02,
                },
                '&.Mui-disabled .MuiSvgIcon-root': {
                  color: '#A986FF',
                },
              }}
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              disabled={selectedRows.length === 0}
            >
              Remover selecionados
            </Button>

            <Button
              variant="contained"
              sx={{
                backgroundColor: '#FFF',
                color: ThemeColors.purple900,
                border: `1px solid ${ThemeColors.purple900}`,
                padding: '15px',
                height: '40px', // Definindo a altura fixa
                '&:hover': {
                  backgroundColor: ThemeColors.purple900,
                  color: '#FFF',
                },
                '&:disabled': {
                  backgroundColor: '#DDD4FF',
                  color: '#A986FF',
                  border: `none`,
                },
              }}
              startIcon={<BeenhereIcon />}
              onClick={handleRowSelection}
              disabled={selectedRows.length === 0}
            >
              Manter selecionados
            </Button>

            <Button
              variant="contained"
              sx={{
                backgroundColor: '#FFF',
                color: ThemeColors.purple900,
                border: `1px solid ${ThemeColors.purple500}`,
                padding: '15px',
                height: '40px', // Definindo a altura fixa
                '&:hover': {
                  backgroundColor: ThemeColors.purple900,
                  color: '#FFF',
                },
                '&:disabled': {
                  backgroundColor: '#DDD4FF',
                  color: '#A986FF',
                  border: `none`,
                },
                '&.Mui-disabled .MuiSvgIcon-root': {
                  color: '#A986FF',
                },
              }}
              onClick={handleRevert}
              startIcon={<UndoIcon />}
              disabled={history.length === 0}
            >
              Reverter Edições
            </Button>
          </>
        ) : null
      }
    </GridToolbarContainer>
  );

  const styledColumns = [
    { field: 'id', headerName: 'ID', flex: 1 },
    ...columns,
  ].map(col => ({
    ...col,
    headerClassName: 'super-app-theme--header',
    headerAlign: 'center',
  }));

  const startIndex = showReceived ? receivedPaginationModel.page * receivedPaginationModel.pageSize : paginationModel.page * paginationModel.pageSize;
  const endIndex = showReceived ? startIndex + receivedPaginationModel.pageSize : startIndex + paginationModel.pageSize;
  const activePaginationModel = showReceived ? receivedPaginationModel : paginationModel;

  const paginatedRows = showReceived
    ? receivedMessages.rows.slice(startIndex, endIndex)
    : currentRows.slice(startIndex, endIndex);
  const activeColumns = showReceived && Array.isArray(receivedMessages.columns)
    ? [
      { field: 'id', headerName: 'ID', flex: 1 },
      ...receivedMessages.columns
    ]
    : styledColumns;

  const fetchDataToNewTable = async (pageToFetch) => {
    setLoading(true);

    const session = await fetchAuthSession({ forceRefresh: true });

    const token = session.tokens.accessToken.toString();
    const client_id = session.tokens.idToken.toString();

    const body = {
      start_date: dayjs(startDate).format('DD/MM/YYYY'),
      end_date: dayjs(endDate).format('DD/MM/YYYY'),
      refresh: true,
      page: pageToFetch,
      name_campaign: campaignFilterName,
    };

    const url = process.env.REACT_APP_BACKEND_AWS_URL;

    return axios
      .post(`${url}/dashboard/messages/sent/lines`, body, {
        headers: {
          Authorization: token,
          "Client_ID": client_id
        }
      })
      .then((response) => {
        const formattedData = response.data.data.map((item, index) => ({
          id: (pageToFetch - 1) * 1000 + index + 1,
          'Título da campanha': item['titulo_da_campanha'],
          'Data de envio': item['data_de_envio'],
          'Modelo da mensagem': item['modelo_da_mensagem'],
          'Contato destinatário': item['contato_destinatario'],
          'Último Status': item['ultimo_status'],
          'Tipo de canal': item['tipo_de_canal'],
          'Data de início da campanha': item['data_de_inicio_da_campanha'],
          'Data de fim da campanha': item['data_de_fim_da_campanha'],
          'ID campanha enviada': item['id_campanha_enviada'],
          'ID da Mensagem': item['id_da_mensagem'],
          'Ordem da mensagem': item['ordem_da_mensagem'],
          ...item
        }));

        setRows((prevRows) => {
          const newRows = [...prevRows, ...formattedData];
          setCurrentRows(newRows);
          return newRows;
        });

        fetchedApiPages.current.add(pageToFetch);
      })
      .catch((error) => {
        console.error('Erro ao buscar os indicadores:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box flex={1} sx={{ overflowX: 'auto', maxWidth: "100%", minWidth: "100%" }}>
      {
        loading ? (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress style={{ color: ThemeColors.purple500 }} />
          </Box>
        ) : (
          <DataGrid
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                paddingTop: '20px'
              },
              '& .super-app-theme--header': {
                backgroundColor: ThemeColors.purple900,
                color: '#fff',
                fontWeight: 'bold',
                fontSize: { xs: '13px', sm: '16px' },
              },
              '& .MuiDataGrid-columnHeaderCheckbox': {
                backgroundColor: ThemeColors.purple900,
              },
              '& .Mui-selected': {
                backgroundColor: '#DDD4FF !important',
                color: '#300D68',
              },
              '& .MuiDataGrid-row': {
                backgroundColor: '#fff',
                color: '#000',
              },
              '& .MuiDataGrid-row:nth-child(n+2)': {
                marginTop: '5px',
              },
              '& .MuiDataGrid-cell': showReceived && {
                justifyContent: 'center',
                textAlign: 'center',
              },
              '& .MuiDataGrid-columnHeader': showReceived && {
                justifyContent: 'center',
                textAlign: 'center',
              },
              border: 'none',
            }}
            rows={paginatedRows}
            columns={activeColumns.map(col => ({
              ...col,
              headerClassName: 'super-app-theme--header',
              headerAlign: 'center',
            }))}
            checkboxSelection={showReceived === false}
            onRowSelectionModelChange={(newSelection) => {
              if (showReceived === false) setSelectedRows(newSelection);
            }}
            rowCount={sentMessagesLength}
            pagination
            paginationMode="server"
            paginationModel={activePaginationModel}
            onPaginationModelChange={(newModel) => {
              if (showReceived) {
                setReceivedPaginationModel(newModel);
              } else {
                const { page, pageSize } = newModel;

                const startIndex = page * pageSize;
                const endIndex = startIndex + pageSize;

                const apiPageSize = 999;
                const lastItemIndexOfCurrentApiPage = apiPage * apiPageSize;

                const isGoingForward = page > paginationModel.page;
                const isExibindoUltimoItemDaApiPage = endIndex >= lastItemIndexOfCurrentApiPage;

                if (isGoingForward && isExibindoUltimoItemDaApiPage && apiPage < totalApiPages) {
                  setLoading(true);
                  fetchDataToNewTable(apiPage + 1).then(() => {
                    setApiPage(apiPage + 1);
                    setPaginationModel(newModel);
                  });
                } else {
                  setPaginationModel(newModel);
                }

              }
            }}
            rowSelectionModel={selectedRows}
            slots={{ toolbar: CustomToolbar }}
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            initialState={{
              pagination: { pageSize: 10 },
              density: 'comfortable',
            }}
          />
        )
      }
    </Box>
  );
}
